import { useMemo } from 'react'

import type { CustomReaction } from '@tribeplatform/gql-client/types'

import { useCMS } from '../CMS/hooks/useCMS.js'
import { sanitizePostType } from '../CMS/utils/utils.js'
import { EntityImage } from '../common/components/EntityImage.js'
import { IconEmoji } from '../common/components/IconEmoji.js'
import { convertIdToGlyph } from '../MediaPicker/utils/utils.js'

interface Props {
  reactionKey?: string
  postTypeId?: string
}
export const ReactionIcon = ({ reactionKey, postTypeId }: Props) => {
  const { allCMSTypes } = useCMS()
  const { activeGlyphId, glyphId, activeColor } = useMemo(() => {
    const maybePostType = allCMSTypes?.find(({ id }) => id === postTypeId)
    const sanitizedPostType = sanitizePostType(maybePostType)
    const hasAllEmojis =
      sanitizedPostType?.allowedReactions === null &&
      sanitizedPostType?.customReactions?.length === 0

    if (hasAllEmojis) {
      return {
        activeGlyphId: reactionKey,
        glyphId: reactionKey,
        activeColor: undefined,
      }
    }

    return (
      sanitizedPostType?.customReactions?.find(
        postType => postType?.key === reactionKey,
      ) ?? ({} as CustomReaction)
    )
  }, [postTypeId, allCMSTypes, reactionKey])

  const glyph = useMemo(
    () => convertIdToGlyph(activeGlyphId || glyphId),
    [activeGlyphId, glyphId],
  )

  if (!activeGlyphId && !glyphId) {
    return <IconEmoji text="+1" />
  }

  if (!glyph) {
    return <IconEmoji text={reactionKey} />
  }

  return (
    <div className="w-full h-full flex items-center justify-center">
      <EntityImage
        entity={{
          image: glyph,
          name: 'Reaction',
        }}
        size="sm"
        displayColor={activeColor}
        iconStyle="solid"
      />
    </div>
  )
}
